import type { PremiumGroup } from '../types'

import { Checkbox, Col, Form, Row, Typography } from 'antd'
import cx from 'classnames'

import { currency, formatDate } from '../formatters'

interface GeneralInformationProps {
  group: PremiumGroup
}

export function GeneralInformation({ group }: GeneralInformationProps): JSX.Element {
  return (
    <Col span={8} className='border p-4 rounded-lg'>
      <Typography.Title
        level={5}
        className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray mb-2'
      >
        General information
      </Typography.Title>
      <Row wrap className='gap-5'>
        <Col className='h-full flex-1 min-w-40'>
          <div className='h-full flex flex-col gap-4 justify-start items-start bg-lucent-light-gray rounded-xl text-lucent-text p-6'>
            <div className='flex flex-col w-full'>
              <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray pb-1'>
                Accounts Receivable
              </span>

              <div
                className={cx(
                  'flex flex-row justify-between items-center gap-2 p-2 rounded -mx-2',
                  group.current_ar !== group.open_ar && 'bg-purple-100'
                )}
              >
                <span className='font-lato text-sm font-semibold text-lucent-text'>Current</span>

                <span className='font-lato text-sm font-semibold text-lucent-text'>
                  {currency(group.current_ar)}
                </span>
              </div>

              <div
                className={cx(
                  'flex flex-row justify-between items-center p-2 rounded -m-2',
                  group.current_ar !== group.open_ar && 'bg-purple-100'
                )}
              >
                <span className='font-lato text-sm font-semibold text-lucent-text'>Open</span>

                <span className='font-lato text-sm font-semibold text-lucent-text'>
                  {currency(group.open_ar)}
                </span>
              </div>
            </div>

            <div className='flex flex-col'>
              <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray pb-1'>
                Group #
              </span>

              <span className='font-lato text-sm text-lucent-text'>{group.number}</span>
            </div>

            <div className='flex flex-col'>
              <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray pb-1'>
                Name
              </span>

              <span className='font-lato text-sm text-lucent-text'>{group.name}</span>
            </div>

            <div className='flex flex-col'>
              <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray pb-1'>
                Location
              </span>

              <span className='font-lato text-sm text-lucent-text'>
                {group.location.short_name}
              </span>
            </div>
          </div>
        </Col>
        <Col className='[&_.ant-form-item]:mb-2 flex-1 min-w-40'>
          <Form.Item
            name='effective_date'
            label={
              <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
                Effective Date
              </span>
            }
            tooltip='The day the client starts with Lucent'
          >
            <p className='leading-normal pb-[13px]'>
              {group.effective_date ? (
                formatDate(group.effective_date)
              ) : (
                <span className='text-gray-400'>Not set</span>
              )}
            </p>
          </Form.Item>

          <Form.Item
            name='renewal_date'
            label={
              <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
                Renewal Date
              </span>
            }
            tooltip='The day the current cycle starts with Lucent. The same as Effective Date if the client is new.'
          >
            <p className='leading-normal pb-[13px]'>
              {group.renewal_date ? (
                formatDate(group.renewal_date)
              ) : (
                <span className='text-gray-400'>Not set</span>
              )}
            </p>
          </Form.Item>

          <Form.Item
            name='termination_date'
            label={
              <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
                Termination Date
              </span>
            }
            tooltip='The day the client will cease active use of Lucent services'
          >
            <p className='leading-normal pb-[13px]'>
              {group.termination_date ? (
                formatDate(group.termination_date)
              ) : (
                <span className='text-gray-400'>Not set</span>
              )}
            </p>
          </Form.Item>

          <Form.Item
            name='run_out_date'
            label={
              <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
                Run Out Date
              </span>
            }
            tooltip='The day we stop processing claim information'
          >
            <p className='leading-normal pb-[13px]'>
              {group.run_out_date ? (
                formatDate(group.run_out_date)
              ) : (
                <span className='text-gray-400'>Not set</span>
              )}
            </p>
          </Form.Item>

          <div className='h-2' />

          <Form.Item name='premium_is_active' valuePropName='checked'>
            <Checkbox>Active</Checkbox>
          </Form.Item>

          <Form.Item name='premium_level_funded' valuePropName='checked'>
            <Checkbox disabled>Level Funded</Checkbox>
          </Form.Item>
        </Col>
      </Row>
    </Col>
  )
}
